import { useState, useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import {
  CreditCard,
  Smartphone,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  Loader2,
  ArrowLeft,
  Sparkles,
  Zap,
} from 'lucide-react'
import { api } from '@/lib/api'
import { useT } from '@/lib/i18n'
import { haptic } from '@/lib/telegram'
import { StorefrontPage, StorefrontCard } from '@/components/StorefrontPage'

type PaymentMethodType = 'card' | 'apple_pay' | 'blik'

export function SandboxPaymentPage() {
  const T = useT('orders')
  const navigate = useNavigate()

  // Parse order_id and amount from query parameters
  const searchParams = useMemo(() => new URLSearchParams(window.location.search), [])
  const orderIdParam = searchParams.get('order_id') || ''
  const amountParam = searchParams.get('amount') || '0'
  const orderId = parseInt(orderIdParam, 10)
  const amount = parseFloat(amountParam) || 0

  const [selectedMethod, setSelectedMethod] = useState<PaymentMethodType>('card')
  const [blikCode, setBlikCode] = useState('123456')
  const [cardNumber] = useState('4242 •••• •••• 4242')
  const [cardExpiry] = useState('12/28')
  const [cardCvc] = useState('123')

  const [isLoading, setIsLoading] = useState(false)
  const [status, setStatus] = useState<'idle' | 'success' | 'failed'>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleSimulatePayment = async (action: 'success' | 'fail') => {
    if (!orderId) {
      setErrorMessage('Не вказано номер замовлення (order_id)')
      return
    }

    setIsLoading(true)
    setErrorMessage(null)
    haptic('medium')

    try {
      const response = await api.post<{ ok: boolean; status: string; message?: string }>(
        '/sandbox/pay',
        {
          order_id: orderId,
          action,
        }
      )

      if (action === 'success' && response.ok) {
        setStatus('success')
        haptic('success')
      } else {
        setStatus('failed')
        setErrorMessage(response.message || T.sandboxPayment.failedText)
        haptic('error')
      }
    } catch (err: unknown) {
      setStatus('failed')
      const msg = err instanceof Error ? err.message : 'Помилка симуляції оплати'
      setErrorMessage(msg)
      haptic('error')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <StorefrontPage title={T.sandboxPayment.title}>
      <div className="mx-auto max-w-xl space-y-6 pb-12">
        {/* Top Header Badge */}
        <div className="flex items-center justify-between rounded-2xl border border-amber-200 bg-amber-50/80 p-4 text-amber-900 shadow-sm">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500 text-white shadow">
              <Zap className="h-5 w-5" />
            </span>
            <div>
              <h2 className="text-sm font-bold">{T.sandboxPayment.title}</h2>
              <p className="text-xs text-amber-700">{T.sandboxPayment.subtitle}</p>
            </div>
          </div>
          <span className="rounded-full bg-amber-200/80 px-2.5 py-1 text-[11px] font-semibold text-amber-900 uppercase tracking-wide">
            Test Mode
          </span>
        </div>

        {/* Order Summary Card */}
        <StorefrontCard className="space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <div>
              <p className="text-xs text-slate-500 font-medium">{T.sandboxPayment.orderNumber}</p>
              <p className="text-lg font-bold text-slate-900">
                {orderId ? `#${orderId}` : '—'}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-500 font-medium">{T.sandboxPayment.amount}</p>
              <p className="text-xl font-extrabold text-blue-600">
                {amount > 0 ? `${amount.toLocaleString()} ₴` : '—'}
              </p>
            </div>
          </div>

          {/* Success State */}
          {status === 'success' && (
            <div className="space-y-4 py-4 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 animate-bounce">
                <CheckCircle2 className="h-10 w-10" />
              </div>
              <div className="space-y-1">
                <h3 className="text-lg font-bold text-slate-900">
                  {T.sandboxPayment.successTitle}
                </h3>
                <p className="text-sm text-slate-600">
                  {T.sandboxPayment.successText}
                </p>
              </div>
              <div className="pt-2">
                <button
                  type="button"
                  onClick={() => navigate({ to: '/orders' })}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-5 py-3.5 text-sm font-semibold text-white shadow-md hover:bg-emerald-700 active:scale-[0.98] transition-all"
                >
                  <ArrowLeft className="h-4 w-4" />
                  {T.sandboxPayment.backToOrders}
                </button>
              </div>
            </div>
          )}

          {/* Failed State Alert */}
          {status === 'failed' && (
            <div className="space-y-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-rose-800">
              <div className="flex items-start gap-3">
                <XCircle className="h-5 w-5 shrink-0 text-rose-600 mt-0.5" />
                <div className="space-y-1">
                  <p className="font-semibold text-sm">{T.sandboxPayment.failedTitle}</p>
                  <p className="text-xs opacity-90">{errorMessage || T.sandboxPayment.failedText}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setStatus('idle')}
                className="text-xs font-semibold text-rose-900 underline hover:no-underline"
              >
                {T.sandboxPayment.tryAgain}
              </button>
            </div>
          )}

          {/* Form & Actions when status !== 'success' */}
          {status !== 'success' && (
            <div className="space-y-5 pt-2">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                  {T.sandboxPayment.selectMethod}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {/* Card Option */}
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedMethod('card')
                      haptic('light')
                    }}
                    className={`flex flex-col items-center justify-center gap-1.5 rounded-2xl border p-3 text-center transition-all ${
                      selectedMethod === 'card'
                        ? 'border-blue-600 bg-blue-50/50 text-blue-700 font-semibold ring-2 ring-blue-600/20'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    <CreditCard className="h-5 w-5" />
                    <span className="text-xs">Card</span>
                  </button>

                  {/* Apple Pay Option */}
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedMethod('apple_pay')
                      haptic('light')
                    }}
                    className={`flex flex-col items-center justify-center gap-1.5 rounded-2xl border p-3 text-center transition-all ${
                      selectedMethod === 'apple_pay'
                        ? 'border-blue-600 bg-blue-50/50 text-blue-700 font-semibold ring-2 ring-blue-600/20'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    <Smartphone className="h-5 w-5" />
                    <span className="text-xs">Apple Pay</span>
                  </button>

                  {/* BLIK Option */}
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedMethod('blik')
                      haptic('light')
                    }}
                    className={`flex flex-col items-center justify-center gap-1.5 rounded-2xl border p-3 text-center transition-all ${
                      selectedMethod === 'blik'
                        ? 'border-blue-600 bg-blue-50/50 text-blue-700 font-semibold ring-2 ring-blue-600/20'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    <Sparkles className="h-5 w-5 text-rose-500" />
                    <span className="text-xs">BLIK</span>
                  </button>
                </div>
              </div>

              {/* Method Details Box */}
              {selectedMethod === 'card' && (
                <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span className="font-medium">Test Visa / Mastercard</span>
                    <ShieldCheck className="h-4 w-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Card Number</p>
                    <p className="font-mono text-sm font-bold text-slate-800 tracking-wider">
                      {cardNumber}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <p className="text-slate-400">Exp</p>
                      <p className="font-mono font-semibold text-slate-700">{cardExpiry}</p>
                    </div>
                    <div>
                      <p className="text-slate-400">CVC</p>
                      <p className="font-mono font-semibold text-slate-700">{cardCvc}</p>
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-500">{T.sandboxPayment.cardHint}</p>
                </div>
              )}

              {selectedMethod === 'apple_pay' && (
                <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-center">
                  <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-slate-900 text-white">
                    <Smartphone className="h-5 w-5" />
                  </div>
                  <p className="text-xs text-slate-600">
                    Симуляція швидкої оплати в 1 дотик через Apple Pay / Touch ID.
                  </p>
                </div>
              )}

              {selectedMethod === 'blik' && (
                <div className="space-y-3 rounded-2xl border border-rose-100 bg-rose-50/40 p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-rose-700">BLIK 6-Digit Code</span>
                    <span className="rounded bg-rose-200 px-1.5 py-0.5 text-[10px] font-bold text-rose-800">
                      Sandbox
                    </span>
                  </div>
                  <input
                    type="text"
                    maxLength={6}
                    value={blikCode}
                    onChange={(e) => setBlikCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    className="w-full text-center text-xl font-mono font-bold tracking-widest rounded-xl border border-slate-300 bg-white py-2.5 text-slate-900 focus:border-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-200"
                  />
                  <p className="text-[11px] text-slate-500">{T.sandboxPayment.blikHint}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-2 pt-2">
                <button
                  type="button"
                  disabled={isLoading}
                  onClick={() => handleSimulatePayment('success')}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3.5 text-sm font-bold text-white shadow-md hover:bg-blue-700 active:scale-[0.98] disabled:opacity-60 transition-all"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {T.sandboxPayment.processing}
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4" />
                      {T.sandboxPayment.simulateSuccess}
                    </>
                  )}
                </button>

                <button
                  type="button"
                  disabled={isLoading}
                  onClick={() => handleSimulatePayment('fail')}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-xs font-semibold text-rose-600 hover:bg-rose-50 hover:border-rose-200 active:scale-[0.98] disabled:opacity-60 transition-all"
                >
                  <XCircle className="h-3.5 w-3.5" />
                  {T.sandboxPayment.simulateFail}
                </button>
              </div>
            </div>
          )}
        </StorefrontCard>

        {/* Back Link */}
        <div className="text-center">
          <button
            type="button"
            onClick={() => navigate({ to: '/orders' })}
            className="text-xs font-semibold text-slate-500 hover:text-slate-800"
          >
            ← {T.sandboxPayment.backToOrders}
          </button>
        </div>
      </div>
    </StorefrontPage>
  )
}
